// All reach-in to the iframe-hosted Intercom Messenger. Framework-agnostic. See ../README.md.
const REFUND_PANEL_W = 480 // keep in sync with $refund-modal-w in the SCSS
const REFUND_PANEL_H = 700 // keep in sync with $refund-modal-h in the SCSS
// Gutter left between the panel and the viewport edge when the viewport is the constraint.
const PANEL_MARGIN = 32
// Below this width Intercom's native full-screen layout is what we want, so we don't override.
// Shared by injectHostStyle's media query and applyPanelLayout's guard so the two can't drift.
const PANEL_BREAKPOINT = 480

const FRAME_SEL = '.intercom-messenger-frame'

// Single source of truth for the panel box: the stylesheet and the inline pass both size from it.
const panelSize = () => ({
  w: Math.min(REFUND_PANEL_W, window.innerWidth - PANEL_MARGIN),
  h: Math.min(REFUND_PANEL_H, window.innerHeight - PANEL_MARGIN)
})

export const HOST_STYLE_ID = 'tds-refund-host-lockdown'
const IFRAME_STYLE_ID = 'tds-refund-messenger-lockdown'

// z_index passed to Intercom('update'); it applies +2. See ../README.md § The open animation.
export const INTERCOM_Z_BEHIND = -1
export const INTERCOM_Z_FRONT = 2147483000

export const CONVERSATION_REQUEST_RE = /messenger\/web\/conversations\/(\d+)/

export const clearIntercomClientState = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && /^intercom\./i.test(k)) localStorage.removeItem(k)
    }
  } catch {
    // no-op
  }
}

export const readIntercomStatePath = (appId?: string): string => {
  if (!appId) return ''
  try {
    const raw = localStorage.getItem(`intercom.intercom-state-${appId}`)
    return raw ? JSON.parse(raw)?.router?.location?.pathname ?? '' : ''
  } catch {
    return ''
  }
}

export const isOnConversationRoute = (appId?: string): boolean =>
  /^\/messages\/(conversation|tickets)\/\d+/.test(readIntercomStatePath(appId))

// Panel present AND on the target conversation (specific id for resume, any conversation
// route for a fresh open) — the reveal condition.
export const isPanelOnRoute = (
  appId?: string,
  conversationId?: string
): boolean => {
  if (!isPanelOpen()) return false
  const path = readIntercomStatePath(appId)
  return conversationId
    ? path.endsWith(`/${conversationId}`)
    : /^\/messages\/(conversation|tickets)\//.test(path)
}

export const isPanelOpen = () =>
  typeof document !== 'undefined' &&
  !!document.querySelector('.intercom-messenger-frame')

// Reveal condition for a SPACE (showSpace), which the two predicates above can't express: they
// require a specific `/messages/(conversation|tickets)/<id>` thread, and a space landing page has no
// id — nor a documented path to pin, so a guessed shape would fail silently. Panel presence alone is
// safe here in a way it would NOT be for resume(): no particular conversation is being claimed, so
// there is no wrong thread to reveal.
export const isPanelOnSpace = (): boolean => isPanelOpen()

export const getMessengerDoc = (): Document | null => {
  const iframe = document.querySelector(
    'iframe[name="intercom-messenger-frame"], #intercom-frame'
  ) as HTMLIFrameElement | null
  try {
    return iframe?.contentDocument ?? null
  } catch {
    return null
  }
}

export const getIntercomIframeWindows = (): Window[] => {
  if (typeof document === 'undefined') return []
  const iframes = Array.from(
    document.querySelectorAll(
      '#intercom-frame, iframe[name="intercom-messenger-frame"], #intercom-container iframe'
    )
  ) as HTMLIFrameElement[]
  const wins: Window[] = []
  for (const f of iframes) {
    try {
      if (f.contentWindow) wins.push(f.contentWindow)
    } catch {
      /* cross-origin — skip */
    }
  }
  return wins
}

// Center + size the panel and kill its entrance animation. !important everywhere because Intercom
// writes the frame's geometry and transform as *inline* styles, and an author !important is the only
// thing in the cascade that outranks a non-important inline one. No opacity guard — it blanks the
// chat. Viewport-relative rather than px because this has to be right on the first paint, before any
// observer has measured a frame; applyPanelLayout then takes over. See ../README.md.
export const injectHostStyle = () => {
  if (typeof document === 'undefined') return
  if (document.getElementById(HOST_STYLE_ID)) return
  const { w, h } = panelSize()
  const style = document.createElement('style')
  style.id = HOST_STYLE_ID
  style.textContent = `
    #intercom-container ${FRAME_SEL},
    #intercom-container ${FRAME_SEL} * {
      transition: none !important;
      animation: none !important;
      transform: none !important;
    }
    @media (min-width: ${PANEL_BREAKPOINT}px) {
      #intercom-container ${FRAME_SEL} {
        width: ${w}px !important;
        height: ${h}px !important;
        max-width: ${w}px !important;
        max-height: ${h}px !important;
        inset: calc(50vh - ${h / 2}px) auto auto calc(50vw - ${
    w / 2
  }px) !important;
      }
    }
  `
  document.head.appendChild(style)
}

export const removeHostStyle = () =>
  document.getElementById(HOST_STYLE_ID)?.remove()

// Write only on a real change. Both writers below run from MutationObservers watching the very
// attribute they set, so an unconditional write re-triggers its own observer. Blink happens to skip
// the mutation record when the value is unchanged, which is the only reason this ever terminated —
// but that is an engine optimization, and where it is absent (jsdom, so any test that puts a frame
// in the document) the observer and the writer starve the event loop between them.
const setStyleIfChanged = (
  el: HTMLElement,
  property: string,
  value: string
) => {
  if (
    el.style.getPropertyValue(property) === value &&
    el.style.getPropertyPriority(property) === 'important'
  ) {
    return
  }
  el.style.setProperty(property, value, 'important')
}

const killFrameAnimation = (frame: HTMLElement) => {
  setStyleIfChanged(frame, 'transition', 'none')
  setStyleIfChanged(frame, 'animation', 'none')
  setStyleIfChanged(frame, 'transform', 'none')
}

// Desktop: center + size the panel. Mobile (<PANEL_BREAKPOINT): Intercom's own full-screen layout.
const applyPanelLayout = () => {
  const frame = document.querySelector(FRAME_SEL) as HTMLElement | null
  if (!frame) return
  killFrameAnimation(frame)
  if (window.innerWidth < PANEL_BREAKPOINT) return
  const { w, h } = panelSize()
  const left = Math.round((window.innerWidth - w) / 2)
  const top = Math.round((window.innerHeight - h) / 2)
  setStyleIfChanged(frame, 'width', `${w}px`)
  setStyleIfChanged(frame, 'height', `${h}px`)
  setStyleIfChanged(frame, 'max-width', `${w}px`)
  setStyleIfChanged(frame, 'max-height', `${h}px`)
  setStyleIfChanged(frame, 'inset', `${top}px auto auto ${left}px`)
}

// Run cb now + on every mutation until the signal aborts.
const observeWhileActive = (
  target: Node,
  init: MutationObserverInit,
  cb: () => void,
  signal: AbortSignal
) => {
  if (signal.aborted) return
  cb()
  const observer = new MutationObserver(cb)
  observer.observe(target, init)
  signal.addEventListener('abort', () => observer.disconnect(), { once: true })
}

// Resolve when `predicate` holds or at maxAttempts (fail OPEN so the spinner can't hang).
// Rejects on abort. See ../README.md § Reveal buffer.
export const pollUntil = (
  predicate: () => boolean,
  { signal, intervalMs, maxAttempts }: PollOptions
): Promise<void> =>
  new Promise((resolve, reject) => {
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const onAbort = () => {
      if (timer) clearTimeout(timer)
      reject(new DOMException('aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
    const tick = () => {
      if (signal.aborted) return
      attempts += 1
      if (predicate() || attempts >= maxAttempts) {
        signal.removeEventListener('abort', onAbort)
        resolve()
        return
      }
      timer = setTimeout(tick, intervalMs)
    }
    tick()
  })

export interface PollOptions {
  signal: AbortSignal
  intervalMs: number
  maxAttempts: number
}

// Keep the panel sized + centered across Intercom's re-renders and viewport changes.
export const sizePanel = (signal: AbortSignal) => {
  const frame = document.querySelector(FRAME_SEL)
  if (!frame) return
  observeWhileActive(
    frame,
    { attributes: true, attributeFilter: ['style', 'class'] },
    applyPanelLayout,
    signal
  )
  // The observer only fires on Intercom's own writes, and injectHostStyle baked its px at injection
  // time — without these a rotate/resize mid-refund leaves the panel off-centre or clipped.
  window.addEventListener('resize', applyPanelLayout)
  window.addEventListener('orientationchange', applyPanelLayout)
  signal.addEventListener(
    'abort',
    () => {
      window.removeEventListener('resize', applyPanelLayout)
      window.removeEventListener('orientationchange', applyPanelLayout)
    },
    { once: true }
  )
}

// Kill the entrance animation from the instant the frame is born — before sizePanel's own observer
// can attach — by watching the container.
export const suppressOpenAnimation = (signal: AbortSignal) => {
  const container =
    document.querySelector('#intercom-container') ?? document.body
  observeWhileActive(
    container,
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    },
    () => {
      const frame = document.querySelector(FRAME_SEL) as HTMLElement | null
      if (frame) killFrameAnimation(frame)
    },
    signal
  )
}

// Hide the messenger's back/options/home nav so the guest stays in this one conversation.
// "Go back" matched by prefix (label varies by version/locale); leave × Close working.
export const lockDownChrome = (signal: AbortSignal) => {
  const doc = getMessengerDoc()
  if (!doc?.documentElement) return
  observeWhileActive(
    doc.documentElement,
    { childList: true, subtree: true },
    () => {
      const d = getMessengerDoc()
      if (!d?.head || d.getElementById(IFRAME_STYLE_ID)) return
      const style = d.createElement('style')
      style.id = IFRAME_STYLE_ID
      style.textContent = `
        [aria-label^="Go back"],
        [aria-label="Conversation options"],
        [aria-label="See previous conversations"],
        [aria-label="Newer conversation"],
        [aria-label="Home"] { display: none !important; }
      `
      d.head.appendChild(style)
    },
    signal
  )
}

// Ids Intercom already knows (persisted open-state) — a request for one is a re-sync, not a submit.
export const readKnownConversationIds = (appId?: string): string[] => {
  try {
    const raw = appId
      ? localStorage.getItem(`intercom.intercom-state-${appId}`)
      : null
    const byId = raw ? JSON.parse(raw)?.conversations?.byId : null
    return byId ? Object.keys(byId) : []
  } catch {
    return []
  }
}

// The SDK has no getter for the conversation id, so hook each Intercom iframe's fetch/XHR for the
// conversation-creating request and resolve with THIS tab's id on the guest's submit. Filtered
// against `knownIds` to skip passive re-syncs. See ../README.md § conversationId.
export const captureConversationId = (
  knownIds: Set<string>,
  signal: AbortSignal
): Promise<string> =>
  new Promise((resolve) => {
    if (signal.aborted) return
    let done = false
    const hooked = new WeakSet<Window>()
    // Undo every patch on abort so the host page never keeps stacked interceptors.
    const restore: Array<() => void> = []
    const record = (id: string) => {
      if (done || signal.aborted || knownIds.has(id)) return
      done = true
      resolve(id)
    }
    const hookWindow = (w: Window | null) => {
      if (!w || hooked.has(w)) return
      try {
        const origFetch = w.fetch
        w.fetch = function (this: Window, ...args: Parameters<typeof fetch>) {
          const input = args[0]
          const url =
            typeof input === 'string'
              ? input
              : input instanceof URL
              ? input.href
              : input.url
          const id = url.match(CONVERSATION_REQUEST_RE)?.[1]
          if (id) record(id)
          return origFetch.apply(this, args)
        }
        restore.push(() => {
          w.fetch = origFetch
        })
        // Window's lib type omits XMLHttpRequest (a global var, not a Window member).
        const XHR = (w as Window & { XMLHttpRequest: typeof XMLHttpRequest })
          .XMLHttpRequest
        const origOpen = XHR?.prototype?.open
        if (origOpen) {
          XHR.prototype.open = function (
            this: XMLHttpRequest,
            method: string,
            url: string | URL,
            async = true,
            username?: string | null,
            password?: string | null
          ) {
            const id = String(url).match(CONVERSATION_REQUEST_RE)?.[1]
            if (id) record(id)
            return origOpen.call(this, method, url, async, username, password)
          }
          restore.push(() => {
            XHR.prototype.open = origOpen
          })
        }
        hooked.add(w)
      } catch {
        /* cross-origin — skip */
      }
    }
    const hookAll = () => {
      hookWindow(window)
      getIntercomIframeWindows().forEach(hookWindow)
    }
    hookAll()
    const container =
      document.querySelector('#intercom-container') ?? document.body
    const observer = new MutationObserver(hookAll)
    observer.observe(container, { childList: true, subtree: true })
    signal.addEventListener(
      'abort',
      () => {
        observer.disconnect()
        restore.forEach((r) => r())
      },
      { once: true }
    )
  })
